/**
 * MeshError renders a terminal-failure UI for an in-flight Tripo mesh job.
 *
 * The status comes from `useMeshJobProgress` and selects a default human
 * message; `errorMsg` (and optional `errorCode`) come from the same hook
 * and are appended for context. Retry restarts the job, Dismiss clears
 * the failure from the editor without restarting.
 */

import { useTranslation } from 'react-i18next';
import type { MeshJobStatus } from '@/hooks/useMeshJobProgress';

interface Props {
  status: MeshJobStatus;
  errorMsg?: string;
  errorCode?: number;
  onRetry: () => void;
  onDismiss: () => void;
}

function getStatusMessage(
  status: MeshJobStatus,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  switch (status) {
    case 'failed':
      return t('meshError.messageFailed', {
        defaultValue: 'Mesh generation failed. Try a different image.',
      });
    case 'banned':
      return t('meshError.messageBanned', {
        defaultValue:
          'Image was rejected by the mesh service. Try a different image.',
      });
    case 'expired':
      return t('meshError.messageExpired', {
        defaultValue: "Mesh generation didn't complete. Try again.",
      });
    case 'cancelled':
      return t('meshError.messageCancelled', {
        defaultValue: "Mesh generation didn't complete. Try again.",
      });
    case 'unknown':
      return t('meshError.messageUnknown', {
        defaultValue: "Mesh generation didn't complete. Try again.",
      });
    default:
      return t('meshError.messageGeneric', {
        defaultValue: 'Mesh generation failed.',
      });
  }
}

export function MeshError({
  status,
  errorMsg,
  errorCode,
  onRetry,
  onDismiss,
}: Props) {
  const { t } = useTranslation('common');
  const baseMessage = getStatusMessage(status, t);
  // The backend writes a generic `task-${status}` placeholder when Tripo
  // doesn't supply a message. Suppress it so the UI just shows the
  // status-keyed default rather than appending the placeholder verbatim.
  const detail =
    errorMsg && errorMsg !== `task-${status}` ? ` ${errorMsg}.` : '';
  const codeSuffix = errorCode
    ? ' ' +
      t('meshError.codeSuffix', {
        code: errorCode,
        defaultValue: '(code {{code}})',
      })
    : '';
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-4 p-6 text-center">
      <p className="max-w-md text-sm text-k-ink/80">
        {baseMessage}
        {detail}
        {codeSuffix}
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center gap-2 rounded-full border-2 border-brand-indigo bg-brand-indigo px-4 py-2 text-xs font-bold text-brand-cream hover:bg-brand-indigo-hover"
        >
          {t('meshError.retryButton', { defaultValue: 'Retry' })}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="inline-flex items-center gap-2 rounded-full border-2 border-brand-indigo px-4 py-2 text-xs font-bold text-brand-indigo hover:bg-brand-indigo/10"
        >
          {t('meshError.dismissButton', { defaultValue: 'Dismiss' })}
        </button>
      </div>
    </div>
  );
}

export default MeshError;
